import { LAYOUT_TABS, SYSTEM_STATUS_TABS_TYPES, TEST_ACCOUNTS } from '@silverfort-nodelibs/common/utils/consts';
import { SYSTEM_MODE_HIDDEN_VIEWS } from '@silverfort-nodelibs/common/utils/system-mode-config';
import { isFeatureActive } from '@silverfort-nodelibs/common/utils/common';
import { SUBCATEGORIES } from '../common/categories';
import { IS_NEW_FIELD } from '../common/fields';
import {
    getNotification,
    maxOneEmptyTab,
    mustHaveWallIfDc,
    validate,
    validateAdaptiveBlock,
    validateAdsyncNodesRequiredAndUnique,
    validateAuthnMethodRequired,
    validateFilledCustomInsight,
    validateFilledForwarding,
    validateLdapServerUnique,
    validateNameUnique,
    validateNodesRadiusAccountsUnique,
    validateNoUnassignedDcs,
    validatePacketFwdAddress,
    validatePacketFwdDuplications,
    validatePacketFwdProtocolsLimit,
    validateRadiusClients,
    validateUniqueIdentifier
} from './validators';
import { httpPost } from '../../utils';

export const updateTabField = (fields, tabKey, fieldId, newValue, markIsDirty = true) => {
    const field = fields[tabKey].fields[fieldId];
    const error = validate(newValue, field.validators, field.name);
    const notification = getNotification(newValue, field.notifications);

    return {
        ...fields,
        [tabKey]: {
            ...fields[tabKey],
            fields: {
                ...fields[tabKey].fields,
                [fieldId]: {
                    ...field,
                    value: newValue,
                    error,
                    notification,
                    isDirty: field.initialValue !== newValue && markIsDirty
                }
            },
            error: null
        }
    };
};

export const updateTabFieldValidators = (fields, tabKey, fieldId, newValidators) => {
    const field = fields[tabKey].fields[fieldId];
    const error = validate(field.value, newValidators, field.name);

    return {
        ...fields,
        [tabKey]: {
            ...fields[tabKey],
            fields: {
                ...fields[tabKey].fields,
                [fieldId]: {
                    ...field,
                    validators: newValidators,
                    error
                }
            }
        }
    };
};

export const updateTabFieldArray = (fields, tabKey, fieldId, index, arrayFieldId, newValue) => {
    const newArray = [...fields[tabKey].fields[fieldId].value];
    newArray[index][arrayFieldId].value = newValue;
    newArray[index][arrayFieldId].isDirty = newValue !== newArray[index][arrayFieldId].initialValue;
    return changeArray(fields, tabKey, fieldId, newArray, index, arrayFieldId);
};

export const updateTabFieldArrayValidators = (fields, tabKey, fieldId, index, arrayFieldId, newValidators) => {
    const newArray = [...fields[tabKey].fields[fieldId].value];
    newArray[index][arrayFieldId].validators = newValidators;
    return changeArray(fields, tabKey, fieldId, newArray, index, arrayFieldId);
};

export const addFieldsToArray = (fields, tabKey, fieldId, newFields, pushToEnd = true) => {
    let newArray = [...fields[tabKey].fields[fieldId].value];
    if (pushToEnd) {
        newArray.push(newFields);
    } else {
        newArray = [newFields, ...newArray];
    }
    return changeArray(fields, tabKey, fieldId, newArray, null, null, false);
};

export const removeFromArray = (fields, tabKey, fieldId, index) => {
    const newArray = [...fields[tabKey].fields[fieldId].value];
    const removedField = newArray.splice(index, 1)[0];
    const setIsDirty = !removedField[IS_NEW_FIELD.ID] || !removedField[IS_NEW_FIELD.ID].value;
    return changeArray(fields, tabKey, fieldId, newArray, null, null, true, setIsDirty);
};

export const buildField = (field, value, validators = {}, notifications = {}, searchable = true) => {
    const fieldObj = {
        value: value,
        validators,
        notifications,
        error: null,
        searchable,
        isDirty: false,
        initialValue: value
    };

    if (field.ID) {
        fieldObj.id = field.ID;
    }
    if (field.NAME) {
        fieldObj.name = field.NAME;
    }
    if (field.TAGS) {
        fieldObj.tags = field.TAGS;
    }
    if (field.DESC) {
        fieldObj.desc = field.DESC;
    }
    if (field.SAAS_DESC) {
        fieldObj.saas_desc = field.SAAS_DESC;
    }
    if (field.TOOLTIP) {
        fieldObj.tooltip = field.TOOLTIP;
    }

    return fieldObj;
};

export const buildCertificateField = (field, certData, validators = {}, notifications = {}, searchable = true) => {
    const fieldObj = {
        value: certData,
        validators,
        notifications,
        error: null,
        searchable
    };

    if (field.ID) {
        fieldObj.id = field.ID;
    }
    if (field.NAME) {
        fieldObj.name = field.NAME;
    }
    if (field.TAGS) {
        fieldObj.tags = field.TAGS;
    }
    if (field.DESC) {
        fieldObj.desc = field.DESC;
    }

    return fieldObj;
};

export const calcDirtyTabs = (fields) => {
    const dirtyTabs = [];
    for (const tab of Object.values(fields)) {
        for (const field in tab.fields) {
            if (Array.isArray(tab.fields[field].value) && isDirtyArray(tab.fields[field].value)) {
                dirtyTabs.push(tab.id);
                break;
            } else if (
                tab.fields[field].isDirty ||
                (tab.fields[IS_NEW_FIELD.ID] && tab.fields[IS_NEW_FIELD.ID].value)
            ) {
                dirtyTabs.push(tab.id);
                break;
            }
        }
    }
    return dirtyTabs;
};

export const calcInvalidTabs = (fields) => {
    const invalidTabs = [];
    for (const tab of Object.values(fields)) {
        if (tab.error) {
            invalidTabs.push(tab.id);
            continue;
        }
        for (const field of Object.values(tab.fields)) {
            if (field.error) {
                invalidTabs.push(tab.id);
                break;
            } else if (Array.isArray(field.value)) {
                if (hasErrorInArray(field.value)) {
                    invalidTabs.push(tab.id);
                    break;
                }
            }
        }
    }
    return invalidTabs;
};

export const validateAll = (fields, ignoredFields = {}) => {
    const validatedState = { ...fields };
    for (const tabKey of Object.keys(fields)) {
        const validatedTab = { ...validatedState[tabKey], fields: { ...validatedState[tabKey].fields } };
        if (ignoredFields[validatedTab.id]) {
            const ignoredField = validatedTab.fields[ignoredFields[validatedTab.id]];
            if (ignoredField && ignoredField.value === false) {
                continue;
            }
        }

        for (const fieldId of Object.keys(validatedTab.fields)) {
            const validatedField = { ...validatedTab.fields[fieldId] };
            if (Array.isArray(validatedField.value)) {
                const validatedArray = [...validatedField.value];
                for (const arrayFields of validatedArray) {
                    if (ignoredFields[validatedField.id]) {
                        const ignoredField = arrayFields[ignoredFields[validatedField.id]];
                        if (ignoredField && ignoredField.value === false) {
                            continue;
                        }
                    }
                    for (const arrayFieldId of Object.keys(arrayFields)) {
                        const arrayField = arrayFields[arrayFieldId];
                        if (
                            typeof arrayField === 'object' &&
                            arrayField !== null &&
                            arrayField.value !== undefined &&
                            arrayField.name !== undefined
                        ) {
                            arrayField.error = validate(arrayField.value, arrayField.validators, arrayField.name);
                        }
                    }
                }
                validatedField.value = validatedArray;
            }
            validatedField.error = validate(validatedField.value, validatedField.validators, validatedField.name);
            validatedTab.fields[fieldId] = validatedField;
        }
        validatedState[tabKey] = validatedTab;
    }
    return validatedState;
};

export const validateCrossFields = (validatedState, selectedTab) => {
    for (const tabKey of Object.keys(validatedState)) {
        const validatedTab = validatedState[tabKey];
        if (validatedTab.validators) {
            if (validatedTab.validators.radiusClientOverlapping) {
                const [fieldId, ipAddressFieldId] = validatedTab.validators.radiusClientOverlapping;
                validateRadiusClients(validatedTab.fields[fieldId], ipAddressFieldId);
            }
            if (validatedTab.validators.adaptiveBlock) {
                const [thresholdFieldId, windowSizeFieldId] = validatedTab.validators.adaptiveBlock;
                validateAdaptiveBlock(validatedTab.fields[thresholdFieldId], validatedTab.fields[windowSizeFieldId]);
            }
            if (validatedTab.validators.uniqueIdentifier) {
                const [aadConnectorFieldId, appIdFieldId] = validatedTab.validators.uniqueIdentifier;
                validateUniqueIdentifier(validatedTab.fields[aadConnectorFieldId], appIdFieldId);
            }
            if (validatedTab.validators.filledCustomInsight) {
                const [
                    conditionsFieldId,
                    excludeConditionsFieldId,
                    usersAndGroupsFieldId,
                    excludedUsersAndGroupsFieldId
                ] = validatedTab.validators.filledCustomInsight;
                validateFilledCustomInsight(
                    validatedTab,
                    conditionsFieldId,
                    excludeConditionsFieldId,
                    usersAndGroupsFieldId,
                    excludedUsersAndGroupsFieldId
                );
            }
            if (validatedTab.validators.uniqueName) {
                const { fieldId, caseSensitive, subcategoryId } = validatedTab.validators.uniqueName;

                if (validatedTab.fields[fieldId] !== undefined) {
                    validateNameUnique(validatedState, selectedTab, fieldId, caseSensitive);
                } else {
                    for (const subFieldIndex in validatedTab.fields[subcategoryId].value) {
                        validateNameUnique(
                            validatedTab.fields[subcategoryId].value,
                            subFieldIndex,
                            fieldId,
                            caseSensitive
                        );
                    }
                }
            }

            if (validatedTab.validators.uniqueAccounts) {
                const accountFieldsIds = validatedTab.validators.uniqueAccounts;
                validateNodesRadiusAccountsUnique(validatedState, selectedTab, accountFieldsIds || []);
            }

            if (validatedTab.validators.filledForwarding) {
                const [kerberosFieldId, ntlmFieldId, ldapFieldId, ldapsFieldId] =
                    validatedTab.validators.filledForwarding;
                if (validatedTab.fields[kerberosFieldId] !== undefined) {
                    validateFilledForwarding(validatedTab, kerberosFieldId, ntlmFieldId, ldapFieldId, ldapsFieldId);
                } else {
                    for (const domain of validatedTab.fields[SUBCATEGORIES.PROTECTED_DOMAINS.ID].value) {
                        validateFilledForwarding(domain, kerberosFieldId, ntlmFieldId, ldapFieldId, ldapsFieldId);
                    }
                }
            }
            if (validatedTab.validators.ldapServerUnique) {
                const [ldapId, backupLdapId] = validatedTab.validators.ldapServerUnique;

                if (validatedTab.fields[ldapId] !== undefined) {
                    validateLdapServerUnique(validatedTab, ldapId, backupLdapId);
                } else {
                    for (const domain of validatedTab.fields[SUBCATEGORIES.PROTECTED_DOMAINS.ID].value) {
                        validateLdapServerUnique(domain, ldapId, backupLdapId);
                    }
                }
            }

            if (validatedTab.validators.packetFwdValidations) {
                validatePacketFwdAddress(validatedTab.fields[validatedTab.validators.packetFwdValidations].value);
                validatePacketFwdDuplications(validatedTab.fields[validatedTab.validators.packetFwdValidations].value);
            }

            if (validatedTab.validators.maxAddressesPerProtocol) {
                validatePacketFwdProtocolsLimit(
                    validatedState[SUBCATEGORIES.FAIL_CLOSE.ID],
                    validatedState[SUBCATEGORIES.SELECTIVE_FORWARDING.ID],
                    validatedTab.validators.maxAddressesPerProtocol
                );
            }

            if (validatedTab.validators.adsyncNodesRequiredAndUnique) {
                validateAdsyncNodesRequiredAndUnique(validatedState);
            }

            if (validatedTab.validators.validateAuthnMethodRequired) {
                validateAuthnMethodRequired(validatedTab.fields);
            }

            if (validatedTab.validators.validateNoUnassignedDcs) {
                validateNoUnassignedDcs(validatedState);
            }

            if (validatedTab.validators.mustHaveWallIfDc) {
                const { dcsFieldId, wallsFieldId } = validatedTab.validators.mustHaveWallIfDc;
                mustHaveWallIfDc(validatedTab.fields, dcsFieldId, wallsFieldId);
            }

            if (validatedTab.validators.maxOneEmptyTab) {
                const { fieldIds } = validatedTab.validators.maxOneEmptyTab;
                maxOneEmptyTab(validatedState, fieldIds);
            }
        }
    }
    return validatedState;
};

export const getInvalidTabsByFields = (tabsToFields) => {
    const invalidTabs = [];
    for (const tabIndex in tabsToFields) {
        const tabFields = tabsToFields[tabIndex];
        if (!validateFields(tabFields)) {
            invalidTabs.push(tabIndex);
        }
    }
    return invalidTabs;
};

export const getDirtyTabsByFields = (tabsToFields) => {
    const dirtyTabs = [];
    for (const tabIndex in tabsToFields) {
        const tabFields = tabsToFields[tabIndex];
        if (
            Object.values(tabFields).some(
                (subcategory) =>
                    (Array.isArray(subcategory.value) && isDirtyArray(subcategory.value)) || subcategory.isDirty
            )
        ) {
            dirtyTabs.push(tabIndex);
        }
    }
    return dirtyTabs;
};

const validateFields = (tabFields) => {
    for (const field of tabFields) {
        if (field.error) {
            return false;
        } else if (Array.isArray(field.value)) {
            if (hasErrorInArray(field.value)) {
                return false;
            }
        }
    }

    return true;
};

const changeArray = (
    fields,
    tabKey,
    fieldId,
    newArray,
    changedIndex,
    changedArrayFieldId,
    validateArrayField = true,
    setIsDirty = false
) => {
    const arrayField = { ...fields[tabKey].fields[fieldId] };
    const validatedArray = [...newArray];

    if (changedArrayFieldId) {
        const field = { ...validatedArray[changedIndex][changedArrayFieldId] };
        field.error = validate(field.value, field.validators, field.name);
        field.notification = getNotification(field.value, field.notifications);
        validatedArray[changedIndex][changedArrayFieldId] = field;
    }

    arrayField.value = validatedArray;
    arrayField.isDirty = arrayField.isDirty || setIsDirty;
    if (validateArrayField) {
        arrayField.error = validate(arrayField.value, arrayField.validators, arrayField.name);
    }
    return {
        ...fields,
        [tabKey]: {
            ...fields[tabKey],
            fields: {
                ...fields[tabKey].fields,
                [fieldId]: arrayField
            },
            error: null
        }
    };
};

export const testAccount = async (username, password, testMetadata, validateDomainName) => {
    const requestData = { username: username, password: password, testMetadata: testMetadata };
    if (validateDomainName) {
        const domainName = username.split('@')[1];
        const error = validateDomainName(domainName);
        if (error) {
            return [false, error, requestData];
        }
    }
    if (
        testMetadata.accountType === TEST_ACCOUNTS.MACHINE_ACCOUNT ||
        testMetadata.accountType === TEST_ACCOUNTS.RADIUS_ACCOUNT
    ) {
        if (!testMetadata.domainId) {
            return [
                false,
                'This node is not connected to any domain, please select domain in this node before testing',
                requestData
            ];
        }
    } else if (!Object.values(TEST_ACCOUNTS).includes(testMetadata.accountType)) {
        return [false, 'Testing for this account is not supported', requestData];
    }

    try {
        const testData = { username: username, password: password, ...testMetadata };

        const result = await httpPost('/Settings/TestAccount', testData);
        const resultsData = result.data;
        if (resultsData && resultsData.result) {
            if (resultsData.name) {
                return [true, resultsData.name, requestData];
            }
            return [true, null, requestData];
        } else if (resultsData && resultsData.reason) {
            return [false, resultsData.reason, requestData];
        } else {
            return [false, 'Test Failed!', requestData];
        }
    } catch (err) {
        let errorMessage = 'Error occurred';
        if (err.response && err.response.data && err.response.data.error) {
            errorMessage = `Error from server: ${err.response.data.error}`;
        }
        return [false, errorMessage, requestData];
    }
};

export const formatJson = (jsonStringVal) => {
    let value = jsonStringVal;
    if (value) {
        try {
            value = JSON.stringify(JSON.parse(value), null, 4);
        } catch {}
    }
    return value;
};

function hasErrorInArray(arr) {
    for (const arrayFields of arr) {
        for (const arrayFieldId of Object.keys(arrayFields)) {
            const arrayField = arrayFields[arrayFieldId];
            if (typeof arrayField === 'object' && arrayField !== null) {
                if (arrayField.error) {
                    return arrayField.error;
                }
            }
        }
    }
    return '';
}

function isDirtyArray(arr) {
    for (const arrayFields of arr) {
        if (arrayFields[IS_NEW_FIELD.ID] && arrayFields[IS_NEW_FIELD.ID].value) {
            return true;
        }
        for (const arrayFieldId of Object.keys(arrayFields)) {
            const arrayField = arrayFields[arrayFieldId];
            if (typeof arrayField === 'object' && arrayField !== null) {
                if (arrayField.isDirty || (arrayField[IS_NEW_FIELD.ID] && arrayField[IS_NEW_FIELD.ID].value)) {
                    return true;
                }
            }
        }
    }
    return '';
}

export const isDirtyTab = (tab) => {
    for (const field in tab.fields) {
        if (Array.isArray(tab.fields[field].value) && isDirtyArray(tab.fields[field].value)) {
            return true;
        } else if (tab.fields[field].isDirty || (tab.fields[IS_NEW_FIELD.ID] && tab.fields[IS_NEW_FIELD.ID].value)) {
            return true;
        }
    }
    return false;
};

export const isDirtySubCategory = (subcategory) => {
    return subcategory.value.some((tabFieldsArray) => {
        return Object.values(tabFieldsArray.fields).some((field) => {
            return field.isDirty;
        });
    });
};

export const clearNotifications = (state, selectedTab, saveAll) => {
    const newState = { ...state };
    for (const tabKey of Object.keys(state)) {
        if (saveAll || tabKey === selectedTab) {
            const newTab = { ...newState[tabKey], fields: { ...newState[tabKey].fields } };
            for (const fieldId of Object.keys(newTab.fields)) {
                const newField = { ...newTab.fields[fieldId] };
                if (Array.isArray(newField.value)) {
                    const newArray = [];
                    for (const arrayFields of newField.value) {
                        for (const arrayFieldId of Object.keys(arrayFields)) {
                            const arrayField = arrayFields[arrayFieldId];
                            if (typeof arrayField === 'object' && arrayField !== null && arrayField.notification) {
                                arrayField.notification = '';
                            }
                        }
                        newArray.push(arrayFields);
                    }
                    newField.value = newArray;
                    newField.notification = '';
                } else if (newField.notification) {
                    newField.notification = '';
                }

                newTab.fields[fieldId] = newField;
            }
            newState[tabKey] = newTab;
        }
    }
    return newState;
};

export const getNewDomainsFromFile = (fileContent, domainsConfigurations) => {
    let jsonContent = '';
    try {
        jsonContent = JSON.parse(fileContent);
    } catch {
        return { error: 'Failed to parse file' };
    }

    if (!Array.isArray(jsonContent)) {
        jsonContent = [jsonContent];
    }

    const currentDomains = new Map();
    Object.values(domainsConfigurations).forEach((domain) => {
        let shortDomains = [];
        if (currentDomains.has(domain.DomainName)) {
            shortDomains = currentDomains.get(domain.DomainName);
        }

        shortDomains.push(domain.DomainShortName);
        currentDomains.set(domain.DomainName, shortDomains);
    });
    const newDomains = [];
    const ldapServers = new Set();
    for (const newDomain of jsonContent) {
        const domainName = newDomain['Domain Name'];
        const shortDomainName = newDomain['Domain Short Name'];

        if (newDomain.DCs) {
            Object.values(newDomain.DCs).forEach((dc) => ldapServers.add(dc));
        }
        let shortDomains = [];
        if (currentDomains.has(domainName)) {
            shortDomains = currentDomains.get(domainName);
            if (shortDomains.includes(shortDomainName)) {
                continue;
            }
        }
        newDomains.push({ DomainName: domainName, DomainShortName: shortDomainName, LdapAuthUrl: '' });
        shortDomains.push(shortDomainName);
        currentDomains.set(domainName, shortDomains);
    }
    return { newDomains: newDomains, ldapServers: ldapServers };
};

const CLOUD_ONLY_SYSTEM_MODE_HIDDEN_VIEWS = {
    hiddenTabs: new Set([
        SYSTEM_STATUS_TABS_TYPES.DCS,
        SYSTEM_STATUS_TABS_TYPES.NODES,
        SYSTEM_STATUS_TABS_TYPES.AD_CONNECT,
        SYSTEM_STATUS_TABS_TYPES.PUSH_SERVICES,
        SYSTEM_STATUS_TABS_TYPES.ADMIN_CONSOLE
    ])
};

export const isSystemTabHiddenBySystemMode = (fieldName, systemMode, isCloudOnly) => {
    return (
        SYSTEM_MODE_HIDDEN_VIEWS[LAYOUT_TABS.SYSTEM_STATUS.ID][systemMode].hiddenTabs.has(fieldName) ||
        (isCloudOnly && CLOUD_ONLY_SYSTEM_MODE_HIDDEN_VIEWS.hiddenTabs.has(fieldName))
    );
};

export const isFieldHiddenBySystemMode = (fieldName, systemMode) => {
    return SYSTEM_MODE_HIDDEN_VIEWS[LAYOUT_TABS.SYSTEM_SETUP.ID][systemMode].fields.has(fieldName);
};

export const filterFieldsBySystemMode = (fields, systemMode) => {
    if (systemMode === undefined) return fields;

    const filteredFields = {};

    for (const fieldName in fields) {
        if (!isFieldHiddenBySystemMode(fieldName, systemMode)) {
            filteredFields[fieldName] = fields[fieldName];
        }
    }

    return filteredFields;
};

export const getFieldValueIfDirty = (field, newValue) => {
    if (isFieldDirty(field)) {
        const isNumericField = field?.validators?.isNumeric;
        if (newValue !== undefined) {
            return isNumericField && !isNaN(Number(newValue)) ? Number(newValue) : newValue;
        }
        return isNumericField && !isNaN(Number(field.value)) ? Number(field.value) : field.value;
    }
};

export const isFieldDirty = (field) => {
    return field.isDirty || (Array.isArray(field.value) && isDirtyArray(field.value));
};

export const isOneFieldOfArrayDirty = (array) => {
    return array.some((field) => isFieldDirty(field));
};

export const defaultFeatureFlagsPredicate = (flagValue) => isFeatureActive(flagValue);

export const isNodeHasProperAz = (node, azConfig) => {
    const nodeDomainIds = Object.keys(node.domainsConfiguration);
    return Object.values(azConfig).find((az) => {
        const azDomainsIds = az.domains;
        const nodeAndAzDomainsIds = new Set(nodeDomainIds, azDomainsIds);
        return (
            az.walls.find((wallId) => wallId === node.id) &&
            nodeDomainIds.length === azDomainsIds.length &&
            nodeAndAzDomainsIds.size === azDomainsIds.length
        );
    });
};

export const filterServiceBus = (fields, hasServiceBus) => {
    if (hasServiceBus) return fields;
    const filteredFields = { ...fields };
    delete filteredFields[SUBCATEGORIES.MOBILE_MESSAGING_SERVICES.ID];
    return filteredFields;
};
